import React, { useCallback, useContext, useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { TransparentErrorBoundary } from '@confluence/error-boundary/entry-points/TransparentErrorBoundary.ts';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import { UpFlowContext } from '@confluence/change-edition/entry-points/UpFlowProvider.ts';
import { fg } from '@confluence/feature-gating';

import { PERSISTENT_UPGRADE_EDITION_TRIAL_TYPES } from './constants.ts';
import { constructUIEvent } from './constructPersistentUpgradeAnalyticsEvents.ts';
import {
	EditionTrialType2PersistentUpgradeMenuIdMap,
	PersistentUpgradeMenuItem,
	constructPreTrialAnalyticsEventPayload,
	messages,
} from './PersistentUpgradeMenuItem.tsx';
import { useTrialPlanComparisonModalActions } from './TrialPlanComparisonExperiment/useTrialPlanComparisonModal.tsx';

export const usePersistentUpgradeMenuSection = (): {
	heading: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	items: {
		action: () => void;
		content: React.JSX.Element;
	}[];
}[] => {
	const [renderFailed, setRenderFailed] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { currentEdition, cloudId, isSiteAdmin } = useContext(UpFlowContext);

	const { showModal: showTrialComparisonModal } = useTrialPlanComparisonModalActions();

	const onUpgradeMenuItemClick = useCallback(() => {
		createAnalyticsEvent(
			constructUIEvent({
				eventAction: 'clicked',
				...constructPreTrialAnalyticsEventPayload(currentEdition!),
			}),
		).fire();

		void showTrialComparisonModal({
			touchpointId:
				EditionTrialType2PersistentUpgradeMenuIdMap[
					PERSISTENT_UPGRADE_EDITION_TRIAL_TYPES.PRE_TRIAL
				],
		});
	}, [createAnalyticsEvent, currentEdition, showTrialComparisonModal]);

	const shouldRenderPersistentUpgradeMenuSection =
		currentEdition === ConfluenceEdition.FREE &&
		Boolean(isSiteAdmin) &&
		Boolean(cloudId) &&
		!renderFailed;

	if (shouldRenderPersistentUpgradeMenuSection) {
		const menuSection = {
			heading: fg('confluence_lozenge_text_update')
				? messages.persistentUpgradeMenuItemFreePlanTitleUpdated
				: messages.persistentUpgradeMenuItemFreePlanTitle,
			items: [
				{
					action: onUpgradeMenuItemClick,
					content: (
						<TransparentErrorBoundary
							attribution={Attribution.GROWTH_LUNA}
							onError={() => setRenderFailed(true)}
						>
							<PersistentUpgradeMenuItem />
						</TransparentErrorBoundary>
					),
				},
			],
		};
		return [menuSection];
	} else {
		return [];
	}
};
